import React, { useState, useMemo, useCallback } from "react";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Badge } from "../components/ui/badge";

import {
  Calculator,
  BookOpen,
  Lightbulb,
  AlertCircle,
  ArrowRight,
  Copy,
  Check,
  RotateCcw,
  Divide,
  Layers,
  ChevronDown,
  Grid3X3,
  Info
} from 'lucide-react';

// Parse polynomial string to coefficients array
const parsePolynomial = (str) => {
  if (!str || str.trim() === '') return [];
  
  const cleanStr = str.replace(/\s+/g, '').replace(/-/g, '+-').replace(/^\+/, '');
  const terms = cleanStr.split('+').filter(t => t !== '');
  
  const coeffMap = {};
  let maxDegree = 0;
  
  terms.forEach(term => {
    let coef = 1;
    let degree = 0;
    
    if (term === 'x') {
      coef = 1;
      degree = 1;
    } else if (term === '-x') {
      coef = -1;
      degree = 1;
    } else if (term.includes('x^')) {
      const parts = term.split('x^');
      coef = parts[0] === '' ? 1 : parts[0] === '-' ? -1 : parseFloat(parts[0]);
      degree = parseInt(parts[1]);
    } else if (term.includes('x')) {
      const parts = term.split('x');
      coef = parts[0] === '' ? 1 : parts[0] === '-' ? -1 : parseFloat(parts[0]);
      degree = 1;
    } else {
      coef = parseFloat(term);
      degree = 0;
    }
    
    if (!isNaN(coef)) {
      coeffMap[degree] = (coeffMap[degree] || 0) + coef;
      maxDegree = Math.max(maxDegree, degree);
    }
  });
  
  const coeffs = [];
  for (let i = maxDegree; i >= 0; i--) {
    coeffs.push(coeffMap[i] || 0);
  }
  
  return coeffs;
};

// Convert coefficients to polynomial string
const coeffsToString = (coeffs) => {
  if (!coeffs || coeffs.length === 0) return '0';
  
  const terms = [];
  const degree = coeffs.length - 1;
  
  coeffs.forEach((coef, i) => {
    const power = degree - i;
    if (coef === 0) return;
    
    let term = '';
    if (power === 0) {
      term = coef.toString();
    } else if (power === 1) {
      if (coef === 1) term = 'x';
      else if (coef === -1) term = '-x';
      else term = `${coef}x`;
    } else {
      if (coef === 1) term = `x^${power}`;
      else if (coef === -1) term = `-x^${power}`;
      else term = `${coef}x^${power}`;
    }
    terms.push(term);
  });
  
  if (terms.length === 0) return '0';
  
  let result = terms[0];
  for (let i = 1; i < terms.length; i++) {
    if (terms[i].startsWith('-')) {
      result += ` - ${terms[i].substring(1)}`;
    } else {
      result += ` + ${terms[i]}`;
    }
  }
  
  return result;
};

// Polynomial long division
const polynomialDivision = (dividendCoeffs, divisorCoeffs) => {
  if (!dividendCoeffs.length || !divisorCoeffs.length) {
    return { quotient: [], remainder: [], steps: [] };
  }
  
  const steps = [];
  let dividend = [...dividendCoeffs];
  const divisorDegree = divisorCoeffs.length - 1;
  const dividendDegree = dividend.length - 1;
  
  if (dividendDegree < divisorDegree) {
    return { quotient: [0], remainder: dividend, steps: [] };
  }
  
  const quotient = [];
  
  while (dividend.length >= divisorCoeffs.length && dividend.some(c => c !== 0)) {
    const leadCoef = dividend[0] / divisorCoeffs[0];
    quotient.push(leadCoef);
    
    const stepMultiplied = divisorCoeffs.map(c => c * leadCoef);
    
    steps.push({
      currentDividend: [...dividend],
      multiplier: leadCoef,
      subtrahend: stepMultiplied,
      power: dividend.length - divisorCoeffs.length
    });
    
    const newDividend = [];
    for (let i = 0; i < dividend.length; i++) {
      const sub = i < stepMultiplied.length ? stepMultiplied[i] : 0;
      newDividend.push(dividend[i] - sub);
    }
    
    while (newDividend.length > 0 && Math.abs(newDividend[0]) < 1e-10) {
      newDividend.shift();
    }
    
    dividend = newDividend.length > 0 ? newDividend : [0];
    
    steps[steps.length - 1].result = [...dividend];
  }
  
  return {
    quotient: quotient.length > 0 ? quotient : [0],
    remainder: dividend,
    steps
  };
};

// Factorize quadratic
const factorizeQuadratic = (a, b, c) => {
  const discriminant = b * b - 4 * a * c;
  
  if (discriminant < 0) {
    return {
      success: false,
      factors: [],
      roots: [],
      discriminant,
      message: 'No real roots (discriminant < 0)'
    };
  }
  
  const root1 = (-b + Math.sqrt(discriminant)) / (2 * a);
  const root2 = (-b - Math.sqrt(discriminant)) / (2 * a);
  
  const factors = [];
  if (a !== 1) {
    factors.push(`${a}`);
  }
  
  if (root1 === Math.floor(root1)) {
    factors.push(root1 >= 0 ? `(x - ${root1})` : `(x + ${Math.abs(root1)})`);
  } else {
    factors.push(`(x - ${root1.toFixed(4)})`);
  }
  
  if (root2 === Math.floor(root2)) {
    factors.push(root2 >= 0 ? `(x - ${root2})` : `(x + ${Math.abs(root2)})`);
  } else {
    factors.push(`(x - ${root2.toFixed(4)})`);
  }
  
  return {
    success: true,
    factors,
    roots: [root1, root2].sort((a, b) => a - b),
    discriminant,
    message: discriminant === 0 ? 'Perfect square (one repeated root)' : 'Two distinct real roots'
  };
};

// Copy Button Component
const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCopy}>
      {copied ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
    </Button>
  );
};

// Division Visualization Component
const DivisionVisualization = ({ steps, dividend, divisor, quotient, remainder }) => {
  if (!steps || steps.length === 0) return null;
  
  return (
    <div className="space-y-4 p-4 bg-muted/30 rounded-xl" data-testid="division-visualization">
      <h4 className="font-semibold text-foreground flex items-center gap-2">
        <Grid3X3 className="w-4 h-4 text-primary" />
        Long Division Steps
      </h4>
      
      <div className="font-mono text-sm bg-background rounded-lg p-4 overflow-x-auto">
        <div className="min-w-[300px]">
          {/* Header showing divisor and quotient */}
          <div className="flex items-center gap-2 mb-2">
            <span className="text-muted-foreground">Quotient:</span>
            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
              {coeffsToString(quotient)}
            </span>
          </div>
          
          <div className="border-b-2 border-l-2 border-primary/50 pl-4 pb-2 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-sky-600 dark:text-sky-400">{divisor}</span>
              <span className="text-muted-foreground">)</span>
              <span className="text-foreground">{dividend}</span>
            </div>
          </div>
          
          {/* Steps */}
          {steps.map((step, index) => (
            <div key={index} className="mb-4 pl-8">
              <div className="text-xs text-muted-foreground mb-1">
                Step {index + 1}: Divide {coeffsToString(step.currentDividend)} by {divisor}
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-amber-600 dark:text-amber-400">
                  Term: {step.multiplier}x^{step.power}
                </div>
                <div className="text-rose-500 border-b border-rose-500/30 pb-1">
                  - ({coeffsToString(step.subtrahend)})
                </div>
                <div className="text-foreground font-semibold">
                  = {coeffsToString(step.result)}
                </div>
              </div>
            </div>
          ))}
          
          {/* Final result */}
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex items-center gap-2">
              <span className="text-muted-foreground">Remainder:</span>
              <span className="text-violet-600 dark:text-violet-400 font-semibold">
                {coeffsToString(remainder)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

function PolynomialCalculator() {
  const [mode, setMode] = useState('division');
  const [dividend, setDividend] = useState('x^3 + 2x^2 - 5x + 3');
  const [divisor, setDivisor] = useState('x - 1');
  const [polyA, setPolyA] = useState('1');
  const [polyB, setPolyB] = useState('-5');
  const [polyC, setPolyC] = useState('6');
  const [expandedSteps, setExpandedSteps] = useState(['step-1']);

  // Division calculation
  const divisionResult = useMemo(() => {
    const dividendCoeffs = parsePolynomial(dividend);
    const divisorCoeffs = parsePolynomial(divisor);
    return polynomialDivision(dividendCoeffs, divisorCoeffs);
  }, [dividend, divisor]);

  // Factorization calculation
  const factorResult = useMemo(() => {
    const a = parseFloat(polyA) || 0;
    const b = parseFloat(polyB) || 0;
    const c = parseFloat(polyC) || 0;
    return factorizeQuadratic(a, b, c);
  }, [polyA, polyB, polyC]);

  const handleReset = useCallback(() => {
    if (mode === 'division') {
      setDividend('x^3 + 2x^2 - 5x + 3');
      setDivisor('x - 1');
    } else {
      setPolyA('1');
      setPolyB('-5');
      setPolyC('6');
    }
  }, [mode]);

  const quickDivisionExamples = [
    { dividend: 'x^3 + 2x^2 - 5x + 3', divisor: 'x - 1' },
    { dividend: 'x^2 - 4', divisor: 'x + 2' },
    { dividend: '2x^3 - 3x^2 + 4x - 1', divisor: 'x - 2' },
  ];

  const quickFactorExamples = [
    { a: '1', b: '-5', c: '6' },
    { a: '1', b: '0', c: '-9' },
    { a: '2', b: '5', c: '-3' },
  ];

  return (
    <HelmetProvider>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Polynomial Calculator - Long Division & Factorization</title>
          <meta name="description" content="Free polynomial calculator. Perform long division and factorization with step-by-step solutions." />
        </Helmet>
        
        <div className="container mx-auto px-4 py-8" data-testid="polynomial-calculator">
          {/* Page Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 flex items-center justify-center">
                <Divide className="w-6 h-6 text-teal-600 dark:text-teal-400" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Polynomial Calculator</h1>
                <p className="text-muted-foreground">Long Division & Factorization with step-by-step solutions</p>
              </div>
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - Calculator Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:sticky lg:top-24 lg:self-start"
            >
              <Card className="rounded-2xl p-6 border bg-card">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    <span className="font-semibold text-foreground">Calculator</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8" 
                    onClick={handleReset}
                    title="Reset"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>

                {/* Mode Toggle */}
                <Tabs value={mode} onValueChange={setMode} className="mb-6">
                  <TabsList className="w-full">
                    <TabsTrigger value="division" className="flex-1" data-testid="division-tab">
                      <Divide className="w-4 h-4 mr-2" />Division
                    </TabsTrigger>
                    <TabsTrigger value="factorization" className="flex-1" data-testid="factor-tab">
                      <Layers className="w-4 h-4 mr-2" />Factorization
                    </TabsTrigger>
                  </TabsList>
                </Tabs>

                {mode === 'division' ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Dividend (numerator)</Label>
                      <Input
                        value={dividend}
                        onChange={(e) => setDividend(e.target.value)}
                        className="font-mono"
                        placeholder="x^3 + 2x^2 - 5x + 3"
                        data-testid="dividend-input"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Divisor (denominator)</Label>
                      <Input
                        value={divisor}
                        onChange={(e) => setDivisor(e.target.value)}
                        className="font-mono"
                        placeholder="x - 1"
                        data-testid="divisor-input"
                      />
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                      <div className="flex flex-wrap gap-2">
                        {quickDivisionExamples.map((ex, i) => (
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              setDividend(ex.dividend);
                              setDivisor(ex.divisor);
                            }}
                          >
                            Ex {i + 1}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Results */}
                    <div className="pt-4 border-t border-border space-y-3">
                      <Label className="text-sm font-medium text-foreground">Results</Label>
                      
                      <div className="rounded-xl p-4 bg-emerald-500/10 border border-emerald-500/20">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Quotient</span>
                          <CopyButton text={coeffsToString(divisionResult.quotient)} />
                        </div>
                        <span className="font-mono text-sm font-semibold text-emerald-600 dark:text-emerald-400 block">
                          {coeffsToString(divisionResult.quotient)}
                        </span>
                      </div>
                      
                      <div className="rounded-xl p-4 bg-violet-500/10 border border-violet-500/20">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Remainder</span>
                          <CopyButton text={coeffsToString(divisionResult.remainder)} />
                        </div>
                        <span className="font-mono text-sm font-semibold text-violet-600 dark:text-violet-400 block">
                          {coeffsToString(divisionResult.remainder)}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="p-3 bg-muted/50 rounded-lg text-sm text-muted-foreground">
                      Enter coefficients for: <span className="font-mono text-foreground">ax² + bx + c</span>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-sm text-muted-foreground mb-2 block">a (x²)</Label>
                        <Input
                          type="number"
                          value={polyA}
                          onChange={(e) => setPolyA(e.target.value)}
                          className="font-mono text-center"
                          data-testid="coef-a-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground mb-2 block">b (x)</Label>
                        <Input
                          type="number"
                          value={polyB}
                          onChange={(e) => setPolyB(e.target.value)}
                          className="font-mono text-center"
                          data-testid="coef-b-input"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-muted-foreground mb-2 block">c</Label>
                        <Input
                          type="number"
                          value={polyC}
                          onChange={(e) => setPolyC(e.target.value)}
                          className="font-mono text-center"
                          data-testid="coef-c-input"
                        />
                      </div>
                    </div>

                    <div className="text-center p-3 bg-muted/30 rounded-lg">
                      <span className="text-sm text-muted-foreground">Polynomial: </span>
                      <span className="font-mono font-medium text-foreground">
                        {polyA}x² {parseFloat(polyB) >= 0 ? '+' : ''} {polyB}x {parseFloat(polyC) >= 0 ? '+' : ''} {polyC}
                      </span>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground mb-2 block">Quick Examples</Label>
                      <div className="flex flex-wrap gap-2">
                        {quickFactorExamples.map((ex, i) => (
                          <Button
                            key={i}
                            variant="outline"
                            size="sm"
                            className="text-xs"
                            onClick={() => {
                              setPolyA(ex.a);
                              setPolyB(ex.b);
                              setPolyC(ex.c);
                            }}
                          >
                            Ex {i + 1}
                          </Button>
                        ))}
                      </div>
                    </div>

                    {/* Results */}
                    <div className="pt-4 border-t border-border space-y-3">
                      <Label className="text-sm font-medium text-foreground">Results</Label>
                      
                      <div className={`rounded-xl p-4 ${factorResult.success ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-rose-500/10 border border-rose-500/20'}`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">Factored Form</span>
                          {factorResult.success && <CopyButton text={factorResult.factors.join(' × ')} />}
                        </div>
                        <span className={`font-mono text-sm font-semibold block ${factorResult.success ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
                          {factorResult.success ? factorResult.factors.join(' × ') : factorResult.message}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-xl p-3 bg-sky-500/10 border border-sky-500/20">
                          <span className="text-xs text-muted-foreground block mb-1">Root 1</span>
                          <span className="font-mono text-sm font-semibold text-sky-600 dark:text-sky-400">
                            {factorResult.roots[0]?.toFixed(4) || 'N/A'}
                          </span>
                        </div>
                        <div className="rounded-xl p-3 bg-amber-500/10 border border-amber-500/20">
                          <span className="text-xs text-muted-foreground block mb-1">Root 2</span>
                          <span className="font-mono text-sm font-semibold text-amber-600 dark:text-amber-400">
                            {factorResult.roots[1]?.toFixed(4) || 'N/A'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="rounded-xl p-3 bg-muted/50">
                        <span className="text-xs text-muted-foreground block mb-1">Discriminant (b² - 4ac)</span>
                        <span className="font-mono text-sm font-semibold text-foreground">
                          {factorResult.discriminant?.toFixed(4)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            </motion.div>

            {/* Center/Right Column - Visualization & Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Visualization Section */}
              {mode === 'division' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card className="rounded-2xl p-6 border bg-card">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-semibold text-foreground flex items-center gap-2">
                        <Info className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                        Division Visualization
                        <Badge variant="secondary" className="ml-2 text-xs">Step-by-step</Badge>
                      </h2>
                    </div>
                    
                    <DivisionVisualization
                      steps={divisionResult.steps}
                      dividend={dividend}
                      divisor={divisor}
                      quotient={divisionResult.quotient}
                      remainder={divisionResult.remainder}
                    />
                    
                    <div className="mt-4 p-4 bg-teal-500/10 border border-teal-500/20 rounded-xl">
                      <h4 className="font-medium text-foreground mb-2">Final Answer</h4>
                      <div className="font-mono text-sm">
                        <span className="text-muted-foreground">{dividend} ÷ {divisor} = </span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                          {coeffsToString(divisionResult.quotient)}
                        </span>
                        {divisionResult.remainder.some(r => r !== 0) && (
                          <span className="text-muted-foreground">
                            {' + '}
                            <span className="text-violet-600 dark:text-violet-400">({coeffsToString(divisionResult.remainder)})</span>
                            /{divisor}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}

              {mode === 'factorization' && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <Card className="rounded-2xl p-6 border bg-card">
                    <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                      <Layers className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      Factorization Process
                    </h2>
                    
                    <div className="space-y-4">
                      <div className="p-4 bg-muted/30 rounded-xl">
                        <h4 className="text-sm font-medium text-foreground mb-3">Quadratic Formula</h4>
                        <div className="font-mono text-center text-lg py-4 bg-background rounded-lg">
                          x = (-b ± √(b² - 4ac)) / 2a
                        </div>
                      </div>
                      
                      <div className="p-4 bg-muted/30 rounded-xl">
                        <h4 className="text-sm font-medium text-foreground mb-3">Substituting Values</h4>
                        <div className="space-y-2 font-mono text-sm">
                          <div>a = {polyA}, b = {polyB}, c = {polyC}</div>
                          <div>Discriminant = ({polyB})² - 4({polyA})({polyC}) = {factorResult.discriminant?.toFixed(4)}</div>
                          {factorResult.success && (
                            <>
                              <div>x₁ = (-({polyB}) + √{factorResult.discriminant?.toFixed(4)}) / (2 × {polyA}) = {factorResult.roots[1]?.toFixed(4)}</div>
                              <div>x₂ = (-({polyB}) - √{factorResult.discriminant?.toFixed(4)}) / (2 × {polyA}) = {factorResult.roots[0]?.toFixed(4)}</div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className={`p-4 rounded-xl ${factorResult.discriminant > 0 ? 'bg-emerald-500/10 border border-emerald-500/20' : factorResult.discriminant === 0 ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-rose-500/10 border border-rose-500/20'}`}>
                        <h4 className="text-sm font-medium text-foreground mb-2">Discriminant Analysis</h4>
                        <p className="text-sm text-muted-foreground">
                          {factorResult.discriminant > 0 && 'Discriminant > 0: Two distinct real roots exist.'}
                          {factorResult.discriminant === 0 && 'Discriminant = 0: One repeated real root (perfect square).'}
                          {factorResult.discriminant < 0 && 'Discriminant < 0: No real roots. Complex roots exist.'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              )}

              {/* Educational Content - What Is Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
              >
                <Card className="rounded-2xl p-6 border bg-card">
                  <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                    <BookOpen className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                    Understanding Polynomials
                  </h2>
                  
                  <div className="space-y-6">
                    {/* Long Division */}
                    <div className="p-4 rounded-xl bg-teal-500/10 border border-teal-500/20">
                      <h3 className="font-bold text-lg text-teal-600 dark:text-teal-400 mb-2 flex items-center gap-2">
                        <Divide className="w-5 h-5" /> Polynomial Long Division
                      </h3>
                      <p className="text-muted-foreground mb-3">
                        <strong>Simple Definition:</strong> Dividing one polynomial by another, just like long division with numbers!
                      </p>
                      <div className="bg-background rounded-lg p-3 mb-3">
                        <p className="text-sm">The process follows these steps:</p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>1. Divide the leading term of dividend by leading term of divisor</li>
                          <li>2. Multiply the entire divisor by this result</li>
                          <li>3. Subtract from the dividend</li>
                          <li>4. Repeat with the remainder until degree is less than divisor</li>
                        </ul>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        <strong>Formula:</strong> Dividend = Divisor × Quotient + Remainder
                      </p>
                    </div>
                    
                    {/* Factorization */}
                    <div className="p-4 rounded-xl bg-sky-500/10 border border-sky-500/20">
                      <h3 className="font-bold text-lg text-sky-600 dark:text-sky-400 mb-2 flex items-center gap-2">
                        <Layers className="w-5 h-5" /> Polynomial Factorization
                      </h3>
                      <p className="text-muted-foreground mb-3">
                        <strong>Simple Definition:</strong> Breaking down a polynomial into simpler factors that multiply together to give the original.
                      </p>
                      <div className="bg-background rounded-lg p-3 mb-3">
                        <p className="text-sm">For quadratics ax² + bx + c:</p>
                        <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                          <li>• Find roots using the quadratic formula</li>
                          <li>• If roots are r₁ and r₂: factors are a(x - r₁)(x - r₂)</li>
                          <li>• The discriminant (b² - 4ac) tells us about the roots</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>

              {/* Step-by-Step Solution */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <Card className="rounded-2xl p-6 border bg-card">
                  <h2 className="font-semibold text-foreground flex items-center gap-2 mb-4">
                    <BookOpen className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                    Step-by-Step Solution
                  </h2>

                  <Accordion type="multiple" value={expandedSteps} onValueChange={setExpandedSteps}>
                    <AccordionItem value="step-1" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">1</span>
                          <span className="font-medium">Identify the polynomials</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          {mode === 'division' ? (
                            <>
                              <p className="text-muted-foreground">
                                Dividend: <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">{dividend}</span>
                              </p>
                              <p className="text-muted-foreground">
                                Divisor: <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">{divisor}</span>
                              </p>
                            </>
                          ) : (
                            <p className="text-muted-foreground">
                              Polynomial: <span className="font-mono text-foreground font-semibold bg-secondary px-2 py-0.5 rounded">
                                {polyA}x² {parseFloat(polyB) >= 0 ? '+' : ''} {polyB}x {parseFloat(polyC) >= 0 ? '+' : ''} {polyC}
                              </span>
                            </p>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-2" className="border-b border-border">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">2</span>
                          <span className="font-medium">Apply the method</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11 space-y-3">
                          {mode === 'division' ? (
                            <div className="bg-secondary/50 rounded-lg p-4">
                              <p className="text-sm font-medium text-foreground mb-2">Long Division Process:</p>
                              <p className="text-sm text-muted-foreground">
                                We repeatedly divide the leading terms and subtract until the remainder has a smaller degree than the divisor.
                              </p>
                            </div>
                          ) : (
                            <div className="bg-secondary/50 rounded-lg p-4">
                              <p className="text-sm font-medium text-foreground mb-2">Quadratic Formula:</p>
                              <p className="font-mono text-sm text-center py-2">
                                x = (-{polyB} ± √({polyB}² - 4×{polyA}×{polyC})) / (2×{polyA})
                              </p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="step-3" className="border-0">
                      <AccordionTrigger className="hover:no-underline">
                        <div className="flex items-center gap-3">
                          <span className="w-6 h-6 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">3</span>
                          <span className="font-medium">Final result</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="pl-11">
                          {mode === 'division' ? (
                            <div className="space-y-2">
                              <div className="bg-emerald-500/10 rounded-lg p-3">
                                <span className="text-sm text-muted-foreground">Quotient: </span>
                                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                                  {coeffsToString(divisionResult.quotient)}
                                </span>
                              </div>
                              <div className="bg-violet-500/10 rounded-lg p-3">
                                <span className="text-sm text-muted-foreground">Remainder: </span>
                                <span className="font-mono font-semibold text-violet-600 dark:text-violet-400">
                                  {coeffsToString(divisionResult.remainder)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <div className={`rounded-lg p-3 ${factorResult.success ? 'bg-emerald-500/10' : 'bg-rose-500/10'}`}>
                                <span className="text-sm text-muted-foreground">Factored form: </span>
                                <span className={`font-mono font-semibold ${factorResult.success ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-500'}`}>
                                  {factorResult.success ? factorResult.factors.join(' × ') : factorResult.message}
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </Card>
              </motion.div>

              {/* Common Mistakes */}
              <Card className="rounded-2xl p-6 bg-rose-500/5 border border-rose-500/20">
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-rose-500" />
                  Common Mistakes to Avoid
                </h2>
                <ul className="space-y-3">
                  {[
                    'Forgetting to include all terms (even those with zero coefficients) when setting up the division',
                    'Sign errors when subtracting during long division - watch those negatives!',
                    'In factorization, forgetting that a ≠ 0 for a quadratic equation',
                    'Confusing factors with roots: if r is a root, then (x - r) is a factor, not (x + r)',
                    'Not checking your answer by multiplying the factors back together',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-rose-500 mt-2 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              {/* FAQ Section */}
              <Card className="rounded-2xl p-6 border bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Frequently Asked Questions</h2>
                <Accordion type="single" collapsible className="w-full">
                  {[
                    {
                      q: 'When should I use polynomial long division?',
                      a: 'Use it when dividing polynomials to find quotient and remainder, simplifying rational expressions, or when finding factors of higher-degree polynomials.'
                    },
                    {
                      q: 'What does the discriminant tell us?',
                      a: 'If discriminant > 0: two distinct real roots. If = 0: one repeated root (perfect square). If < 0: no real roots (complex roots).'
                    },
                    {
                      q: 'Can all polynomials be factored?',
                      a: 'Over real numbers, not all polynomials factor nicely. Some have irrational or complex roots. Over complex numbers, every polynomial can be factored completely.'
                    },
                    {
                      q: 'What is synthetic division?',
                      a: 'A shortcut method for dividing by linear factors (x - c). It\'s faster than long division but only works for divisors of the form (x - c).'
                    },
                  ].map((faq, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-b border-border">
                      <AccordionTrigger className="text-left hover:no-underline">
                        <span className="font-medium text-foreground">{faq.q}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </Card>

              {/* Real World Applications */}
              <Card className="rounded-2xl p-6 border bg-card">
                <h2 className="text-xl font-bold text-foreground mb-4">Real-World Applications</h2>
                <ul className="space-y-3">
                  {[
                    'Engineering: Analyzing control systems and signal processing using transfer functions',
                    'Physics: Modeling projectile motion and wave behavior with quadratic equations',
                    'Economics: Profit optimization and cost analysis often involve polynomial functions',
                    'Computer Graphics: Bezier curves and splines for smooth animations and design',
                    'Statistics: Polynomial regression for fitting complex data patterns',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <ArrowRight className="w-4 h-4 text-teal-500 mt-1 flex-shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </HelmetProvider>
  );
}

export default PolynomialCalculator;
